import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  SmartToy as ChatGptIcon,
  Science as TesterIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpOutlineIcon,
  Logout,
  Add as AddIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon,
  AccountCircle,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import GuideTour from './GuideTour';
import ChatbotWidget from './ChatbotWidget';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard' },
  { text: 'Contacts', icon: <PeopleIcon />, path: '/app/contacts' },
  { text: 'Messages', icon: <ForumIcon />, path: '/app/messages' },
  { text: 'Comments', icon: <MessageIcon />, path: '/app/comments' },
  // Temporarily hidden
  // { text: 'Auto Replies', icon: <AutoReplyIcon />, path: '/app/autoreplies' },
  { text: 'Agent Prompts', icon: <ChatGptIcon />, path: '/app/chatgpt-prompts' },
  { text: 'Prompt Tester', icon: <TesterIcon />, path: '/app/prompt-tester' },
];

const settingsItem = { text: 'Settings', icon: <SettingsIcon />, path: '/app/settings' };
const helpItem = { text: 'Help Center', icon: <HelpOutlineIcon />, path: '/app/help-center' };

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showWelcomeTooltip, setShowWelcomeTooltip] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const connectBtnRef = useRef<HTMLButtonElement | null>(null);

  // Check if user has active subscription
  const hasActiveSubscription = user && user.subscriptionStatus === 'active' && user.subscriptionPlan && user.subscriptionPlan !== 'free';

  // Show welcome tooltip for new users on first dashboard visit
  useEffect(() => {
    if (location.pathname === '/app/dashboard' && user) {
      // Check if user has any connected accounts
      const hasConnectedAccounts = user.facebookPageId || user.instagramId || user.whatsappBusinessAccountId;
      
      // Check if tour has already been shown for this user
      const tourShownKey = `tour_shown_${user.username}`;
      const hasTourBeenShown = localStorage.getItem(tourShownKey);
      
      if (!hasConnectedAccounts && !hasTourBeenShown) {
        // Show tooltip after a short delay to ensure everything is rendered
        const timer = setTimeout(() => {
          setShowWelcomeTooltip(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleConnectClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleConnectClose = () => {
    setAnchorEl(null);
  };


  // Listen for global event to open connect dropdown (from other pages)
  useEffect(() => {
    const handler = () => {
      if (connectBtnRef.current) {
        setAnchorEl(connectBtnRef.current);
      }
    };
    window.addEventListener('open-connect-dropdown', handler as EventListener);
    return () => window.removeEventListener('open-connect-dropdown', handler as EventListener);
  }, []);

  const handleSubscriptionRequired = () => {
    handleConnectClose();
    navigate('/app/checkout?source=connect');
  };

  const handleFacebookConnect = () => {
    if (!hasActiveSubscription) {
      handleSubscriptionRequired();
      return;
    }
    setIsConnecting(true);
    handleConnectClose();
    
    // Small delay to show loading state
    setTimeout(() => {
      const baseUrl = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8080';
      window.location.href = `${baseUrl}/auth/facebook`;
    }, 500);
  };

  const handleInstagramConnect = () => {
    if (!hasActiveSubscription) {
      handleSubscriptionRequired();
      return;
    }
    handleConnectClose();
    const baseUrl = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8080';
    window.location.href = `${baseUrl}/auth/instagram`;
  };

  const handleWhatsAppConnect = () => {
    if (!hasActiveSubscription) {
      handleSubscriptionRequired();
      return;
    }
    handleConnectClose();
    
    // WhatsApp Embedded Signup via Facebook JS SDK (Manychat-style)
    if (typeof (window as any).FB === 'undefined') {
      console.error('Facebook SDK not loaded');
      alert('Facebook SDK is not ready. Please refresh the page.');
      return;
    }
    
    (window as any).FB.login((response: any) => {
      if (response.authResponse) {
        const { accessToken } = response.authResponse;
        // Send access token to backend to complete signup
        const baseUrl = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8080';
        fetch(`${baseUrl}/api/whatsapp/complete-signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ accessToken })
        })
          .then(res => res.json())
          .then(data => {
            if (data.success) {
              // Refresh user data and navigate to dashboard
              if ((window as any).checkAuth) (window as any).checkAuth();
              navigate('/app/dashboard');
            } else {
              alert('WhatsApp connection failed: ' + (data.error || 'Unknown error'));
            }
          })
          .catch(err => {
            console.error('WhatsApp signup completion error:', err);
            alert('Failed to complete WhatsApp connection');
          });
      } else {
        console.log('User cancelled login or did not fully authorize.');
      }
    }, {
      scope: 'business_management,whatsapp_business_management,whatsapp_business_messaging',
      extras: {
        setup: {
          // Embedded Signup for WhatsApp
        },
        business_config: {
          business: {
            name: 'Social In One'
          }
        },
        featureType: '',
        sessionInfoVersion: 2
      }
    });
  };

  const handleCloseWelcomeTooltip = () => {
    setShowWelcomeTooltip(false);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo Section */}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Logo height={48} width={'100%'}/>
      </Box>
      <Divider />
      
      {/* Main Navigation - takes available space */}
      <Box sx={{ flex: 1 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      
      {/* Settings section - pushed to absolute bottom */}
      <Box sx={{ mt: 'auto', pb: 2 }}>
        <Divider sx={{ my: 2 }} />
        <List>
          {/* Help Center above Settings */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === helpItem.path}
              onClick={() => {
                navigate(helpItem.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{helpItem.icon}</ListItemIcon>
              <ListItemText primary={helpItem.text} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === settingsItem.path}
              onClick={() => {
                navigate(settingsItem.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{settingsItem.icon}</ListItemIcon>
              <ListItemText primary={settingsItem.text} />
            </ListItemButton>
          </ListItem>
          
          {/* Logout button */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{ color: '#d63031' }}
            >
              <ListItemIcon>
                <Logout sx={{ color: '#d63031' }} />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Modern Loading Overlay */}
      {isConnecting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            color: 'white',
          }}
        >
          {/* Animated Logo */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1877F2 0%, #1666cf 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 0 rgba(24, 119, 242, 0.7)',
                },
                '70%': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 0 0 20px rgba(24, 119, 242, 0)',
                },
                '100%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 0 rgba(24, 119, 242, 0)',
                },
              },
            }}
          >
            <svg viewBox="0 0 24 24" fill="white" style={{ width: 40, height: 40 }}>
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </Box>
          
          {/* Loading Text */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Connecting to Meta...
          </Typography>
          
          <Typography variant="body1" sx={{ opacity: 0.8, textAlign: 'center', maxWidth: 300 }}>
            Please wait while we redirect you to Facebook for account authorization. This may take a few moments.
          </Typography>
          
          {/* Animated Dots */}
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#1877F2',
                  animation: `bounce 1.4s infinite ${i * 0.16}s`,
                  '@keyframes bounce': {
                    '0%, 80%, 100%': {
                      transform: 'scale(0)',
                    },
                    '40%': {
                      transform: 'scale(1)',
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
      
      <Box 
        sx={{ 
          display: 'flex',
          background: '#fafafa',
          minHeight: '100vh',
        }}
      >
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#2c3e50' }}
          >
            <MenuIcon />
          </IconButton>
          {/* Connected Platform Info - render a chip per connected account */}
          {user && (user.facebookPageId || user.instagramId || user.whatsappBusinessAccountId || user.tiktokId) && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, gap: 1 }}>
              {/* Facebook */}
              {user.facebookPageId && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <FacebookIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                </Box>
              )}
              {/* Instagram */}
              {user.instagramId && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <InstagramIcon sx={{ fontSize: 18, color: '#E1306C' }} />
                </Box>
              )}
              {/* WhatsApp */}
              {user.whatsappBusinessAccountId && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                </Box>
              )}
              {/* TikTok */}
              {user.tiktokId && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <Box sx={{ mr: 0.25, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg viewBox="0 0 48 56" fill="none" style={{ width: 20, height: 26 }}>
                      <g>
                        <path d="M41.5 17.5c-3.6 0-6.5-2.9-6.5-6.5V6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#25F4EE"/>
                        <path d="M41.5 19.5c-3.6 0-6.5-2.9-6.5-6.5V8h-4.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-4.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V25.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#FE2C55"/>
                        <path d="M35 13c0 3.6 2.9 6.5 6.5 6.5v-4c-1.9 0-3.5-1.6-3.5-3.5H35z" fill="#25F4EE"/>
                        <path d="M35 11c0 3.6 2.9 6.5 6.5 6.5v-2c-2.5 0-4.5-2-4.5-4.5H35z" fill="#FE2C55"/>
                        <path d="M35 6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8c-3.6 0-6.5-2.9-6.5-6.5V6z" fill="#000"/>
                      </g>
                    </svg>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Connect Button */}
          <Button
            startIcon={<AddIcon />}
            ref={connectBtnRef}
            onClick={handleConnectClick}
            className="guide-connect-button"
            sx={{
              background: 'rgba(95, 213, 182, 0.1)',
              color: '#2c3e50',
              border: '1px solid rgba(95, 213, 182, 0.3)',
              borderRadius: 2,
              px: 2,
              py: 1,
              fontWeight: 500,
              '&:hover': {
                background: 'rgba(95, 213, 182, 0.2)',
              },
            }}
          >
            {user && (user.facebookPageId || user.instagramId || user.whatsappBusinessAccountId || user.tiktokId) ? 
              'Add Account' : 'Connect Account'
            }
          </Button>
          
          {/* Spacer to push everything to the right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* User Account Display */}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, gap: 1 }}>
              {/* Signed in as label */}
              {/* <Typography variant="caption" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '0.75rem',
                mr: 0.5
              }}>
                Connected as:
              </Typography> */}

              {/* Main Username */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(24, 119, 242, 0.1)',
                  border: '1px solid rgba(24, 119, 242, 0.3)',
                  maxWidth: { xs: '28vw', sm: '24vw', md: '20vw', lg: '16vw' },
                  overflow: 'hidden'
                }}
              >
                <AccountCircle sx={{ fontSize: 16, color: '#1877F2' }} />
                <Tooltip title={user.username || ''} arrow>
                  <Typography variant="caption" sx={{ color: '#1877F2', fontWeight: 500, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {user.username}
                  </Typography>
                </Tooltip>
              </Box>

              {/* Facebook Page Name */}
              {user.facebookPageName && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: 'rgba(66, 103, 178, 0.1)',
                    border: '1px solid rgba(66, 103, 178, 0.3)',
                    maxWidth: { xs: '28vw', sm: '24vw', md: '20vw', lg: '16vw' },
                    overflow: 'hidden'
                  }}
                >
                  <FacebookIcon sx={{ fontSize: 16, color: '#4267B2' }} />
                  <Tooltip title={user.facebookPageName || ''} arrow>
                    <Typography variant="caption" sx={{ color: '#4267B2', fontWeight: 500, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                      {user.facebookPageName}
                    </Typography>
                  </Tooltip>
                </Box>
              )}

              {/* Instagram Username */}
              {user.instagramUsername && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: 'rgba(225, 48, 108, 0.1)',
                    border: '1px solid rgba(225, 48, 108, 0.3)',
                    maxWidth: { xs: '28vw', sm: '24vw', md: '20vw', lg: '16vw' },
                    overflow: 'hidden'
                  }}
                >
                  <InstagramIcon sx={{ fontSize: 16, color: '#E1306C' }} />
                  <Tooltip title={user.instagramUsername || ''} arrow>
                    <Typography variant="caption" sx={{ color: '#E1306C', fontWeight: 500, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                      {user.instagramUsername}
                    </Typography>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}

          

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleConnectClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left', // Sola hizalı
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left', // Sola hizalı
            }}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: 2,
                mt: 1,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid rgba(0,0,0,0.05)',
              },
            }}
          >
            {!user?.facebookPageId && (
              <MenuItem onClick={handleFacebookConnect} sx={{ py: 1.5, px: 2, minWidth: 200 }}>
                <FacebookIcon sx={{ mr: 2, color: '#1877F2' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Connect with Meta
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Facebook, Instagram, WhatsApp
                  </Typography>
                </Box>
              </MenuItem>
            )}
            {user?.facebookPageId && (
              <MenuItem sx={{ py: 1.5, px: 2, minWidth: 200, opacity: 0.6, cursor: 'default' }}>
                <FacebookIcon sx={{ mr: 2, color: '#10B981' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#10B981' }}>
                    Meta Already Connected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Facebook, Instagram, WhatsApp
                  </Typography>
                </Box>
              </MenuItem>
            )}
            
            {/* Instagram Connect Option */}
            {!user?.instagramBusinessId && (
              <MenuItem
                onClick={handleInstagramConnect}
                sx={{ py: 1.5, px: 2, minWidth: 200 }}
              >
                <InstagramIcon sx={{ mr: 2, color: '#E1306C' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Connect with Instagram
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Instagram Business Account
                  </Typography>
                </Box>
              </MenuItem>
            )}
            
            {/* Instagram Already Connected */}
            {user?.instagramBusinessId && (
              <MenuItem sx={{ py: 1.5, px: 2, minWidth: 200, opacity: 0.6, cursor: 'default' }}>
                <InstagramIcon sx={{ mr: 2, color: '#10B981' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#10B981' }}>
                    Instagram Already Connected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Instagram Business Account
                  </Typography>
                </Box>
              </MenuItem>
            )}

            {/* WhatsApp Connect (separate flow) */}
            {!user?.whatsappBusinessAccountId && (
              <MenuItem
                onClick={handleWhatsAppConnect}
                sx={{ py: 1.5, px: 2, minWidth: 200 }}
              >
                <WhatsAppIcon sx={{ mr: 2, color: '#25D366' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Connect with WhatsApp
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    WhatsApp Business Account
                  </Typography>
                </Box>
              </MenuItem>
            )}
            {user?.whatsappBusinessAccountId && (
              <MenuItem sx={{ py: 1.5, px: 2, minWidth: 200, opacity: 0.6, cursor: 'default' }}>
                <WhatsAppIcon sx={{ mr: 2, color: '#10B981' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#10B981' }}>
                    WhatsApp Already Connected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.whatsappPhoneNumber ? `+${user.whatsappPhoneNumber}` : 'WhatsApp Business Account'}
                  </Typography>
                </Box>
              </MenuItem>
            )}

            {!user?.tiktokId && (
              <MenuItem
                disabled
                sx={{ py: 1.5, px: 2, minWidth: 200, opacity: 0.6, cursor: 'not-allowed' }}
              >
                <Box sx={{ mr: 2, width: 24, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {/* TikTok Official Logo SVG */}
                  <svg viewBox="0 0 48 56" fill="none" style={{ width: 24, height: 32 }}>
                    <g>
                      <path d="M41.5 17.5c-3.6 0-6.5-2.9-6.5-6.5V6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#25F4EE"/>
                      <path d="M41.5 19.5c-3.6 0-6.5-2.9-6.5-6.5V8h-4.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-4.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V25.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#FE2C55"/>
                      <path d="M35 13c0 3.6 2.9 6.5 6.5 6.5v-4c-1.9 0-3.5-1.6-3.5-3.5H35z" fill="#25F4EE"/>
                      <path d="M35 11c0 3.6 2.9 6.5 6.5 6.5v-2c-2.5 0-4.5-2-4.5-4.5H35z" fill="#FE2C55"/>
                      <path d="M35 6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8c-3.6 0-6.5-2.9-6.5-6.5V6z" fill="#000"/>
                    </g>
                  </svg>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Connect with TikTok
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        backgroundColor: '#f44336',
                        borderRadius: 1,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        color: 'white',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Soon
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    TikTok Business & Ads
                  </Typography>
                </Box>
              </MenuItem>
            )}
            {user?.tiktokId && (
              <MenuItem sx={{ py: 1.5, px: 2, minWidth: 200, opacity: 0.6, cursor: 'default' }}>
                <Box sx={{ mr: 2, width: 24, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {/* TikTok Official Logo SVG */}
                  <svg viewBox="0 0 48 56" fill="none" style={{ width: 24, height: 32 }}>
                    <g>
                      <path d="M41.5 17.5c-3.6 0-6.5-2.9-6.5-6.5V6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#25F4EE"/>
                      <path d="M41.5 19.5c-3.6 0-6.5-2.9-6.5-6.5V8h-4.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-4.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V25.7c1.9 1.1 4.1 1.8 6.5 1.8v-8z" fill="#FE2C55"/>
                      <path d="M35 13c0 3.6 2.9 6.5 6.5 6.5v-4c-1.9 0-3.5-1.6-3.5-3.5H35z" fill="#25F4EE"/>
                      <path d="M35 11c0 3.6 2.9 6.5 6.5 6.5v-2c-2.5 0-4.5-2-4.5-4.5H35z" fill="#FE2C55"/>
                      <path d="M35 6h-6.5v27.5c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5 2.9-6.5 6.5-6.5c.3 0 .7 0 1 .1v-6.6c-.3 0-.7-.1-1-.1-7.2 0-13 5.8-13 13s5.8 13 13 13 13-5.8 13-13V23.7c1.9 1.1 4.1 1.8 6.5 1.8v-8c-3.6 0-6.5-2.9-6.5-6.5V6z" fill="#000"/>
                    </g>
                  </svg>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#10B981' }}>
                    TikTok Already Connected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    TikTok Business & Ads
                  </Typography>
                </Box>
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
        className="guide-navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: '#f5f5f5',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: '#f5f5f5',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
    
    {/* Guide Tour for new users */}
    <GuideTour
      isVisible={showWelcomeTooltip}
      onClose={handleCloseWelcomeTooltip}
      user={user}
    />
    
    {/* Chatbot Widget */}
    <ChatbotWidget />
    </>
  );
};

export default Layout; 